import React, { useEffect, useMemo, useRef, useState, RefObject } from 'react';
import { useTableColumns } from '../../../hooks/useTableColumns';
import { useNavigate, useLocation } from 'react-router-dom';

import { listConsultor, listEquipe, CooperativaData, VoluntarioData, PagedResponse } from '../../../api/comissionamentosApi';
import { usePeriodoComissionamentos } from '../hooks/usePeriodoComissionamentos';
import { Esqueleto } from '../../../components/esqueleto/Esqueleto';
import Paginacao from '../../../components/paginacao/Paginacao';
import FiltrosModal from '../modal/FiltrosModal';
import Filter from '../../../img/filter.svg';
import SearchIcon from '../../../img/search-icon.svg';
import DadosAtualizadosEm from '../components/DadosAtualizadosEm';
// Esqueleto de página compartilhado (`recepcao-conte-do`, `recepcao-header-row`,
// `recepcao-title`). A tela já usava `recepcao-title` sem importar este arquivo — o
// acoplamento invisível que originou o BUG-V74 na at-67. O import torna a dependência real.
import '../../recepcao/pages/Recepcao.css';
import './comissionamentos.css';

interface ComissionamentosProps {
  sidebarRef?: RefObject<HTMLDivElement>;
}

interface ComissionamentoItem {
  id: string;
  codigo: string;
  nome: string;
  contratos: number;
}

type TabType = 'voluntario' | 'cooperativa';

const ComissionamentosPage: React.FC<ComissionamentosProps> = ({ sidebarRef }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<TabType>('cooperativa');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>(''); // Termo efetivo da busca
  // Período em mm-yyyy — fonte única do módulo (sobrevive à navegação e ao F5)
  const [periodo, setPeriodo] = usePeriodoComissionamentos();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [filteredData, setFilteredData] = useState<ComissionamentoItem[]>([]);
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(25);
  const [filtrosAbertos, setFiltrosAbertos] = useState<boolean>(false);
  const [totalItens, setTotalItems] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);

  const loadData = async (search?: string) => {
    setLoading(true);
    setError(null);
    const termoBusca = search !== undefined ? search : searchTerm;

    try {
      if (activeTab === 'cooperativa') {
        const query: any = { page, limit, periodo };
        if (termoBusca.trim()) {
          query.nome = termoBusca.trim();
        }

        const cooperativasResponse: PagedResponse<CooperativaData> = await listConsultor(query);
        setTotalItems(cooperativasResponse.totalItens);
        setTotalPages(cooperativasResponse.totalPages);

        const convertedData: ComissionamentoItem[] = (cooperativasResponse.data || []).map((coop: any) => ({
          id: coop.id,
          codigo: coop.codigoCooperativa,
          nome: coop.nome,
          contratos: coop.contratos_periodo ?? 0,
        }));

        setFilteredData(convertedData);
      } else if (activeTab === 'voluntario') {
        const query: any = { page, limit, periodo };
        if (termoBusca.trim()) {
          query.nome = termoBusca.trim();
        }

        const voluntariosResponse: PagedResponse<VoluntarioData> = await listEquipe(query);
        setTotalItems(voluntariosResponse.totalItens);
        setTotalPages(voluntariosResponse.totalPages);

        const convertedData: ComissionamentoItem[] = (voluntariosResponse.data || []).map((vol: any) => ({
          id: vol.id,
          codigo: vol.codigoVoluntario,
          nome: vol.nome,
          contratos: vol.contratos_periodo ?? 0,
        }));

        setFilteredData(convertedData);
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      setError('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    setSearchQuery('');
    setSearchTerm('');
    setPage(1);
    setError(null);
  };

  const handleSearch = () => {
    setSearchTerm(searchQuery);
    setPage(1);
  };

  // Medição de colunas do padrão do sistema (mesmo hook da Recepção e de Regras): mede o
  // texto real, distribui a sobra do container e soma o padding do casco.
  const headers = ['Código', 'Nome', 'Contratos'];
  const defaultColumnWidths = [120, 320, 140];
  const tableScrollRef = useRef<HTMLDivElement>(null);
  const headerRefs = useRef<(HTMLDivElement | null)[]>([]);
  const tableRows = useMemo(
    () => filteredData.map((item) => [item.codigo, item.nome, String(item.contratos)]),
    [filteredData],
  );
  const { tableColumns, tableWidth } = useTableColumns({
    containerRef: tableScrollRef,
    headerCellsRef: headerRefs,
    headers,
    defaultWidths: defaultColumnWidths,
    rows: tableRows,
    remeasureKey: activeTab,
  });


  /**
   * O clique no nome leva ao detalhe, que abre em Regras — Contratos e Pagamentos agora são
   * abas de lá, não destinos próprios a partir daqui (decisão do Victor, 16/09/2026).
   */
  const abrirDetalhe = (item: ComissionamentoItem) => {
    const qualificadoTipo = activeTab === 'cooperativa' ? 'COOPERATIVA' : 'VOLUNTARIO';
    navigate('/comissionamentos/regras', {
      state: { item, origem: activeTab, qualificado_tipo: qualificadoTipo, qualificado_uuid: item.id },
    });
  };

  useEffect(() => {
    // Recarrega ao trocar de aba, termo de busca efetivo, página ou período
    loadData();
    // Justificativa do disable: o array abaixo já lista todos os valores reativos
    // que loadData lê e que podem mudar (activeTab, searchTerm, page, periodo e
    // limit — este deixou de ser fixo em 25 quando a tela ganhou "Itens por página").
    // Incluir a própria loadData faria o efeito rodar a cada render — ela é recriada
    // em todo render e chama setState —, resultando em loop infinito de requisições.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, searchTerm, page, periodo, limit]);

  useEffect(() => {
    const st: any = location.state;
    if (st?.defaultTab) {
      const dt = st.defaultTab as string;
      if (dt === 'cooperativa' || dt === 'voluntario') {
        setActiveTab(dt as TabType);
      } else {
        setActiveTab('cooperativa');
      }
    }
    // O período não vem mais pela navegação: ele é do módulo (usePeriodoComissionamentos).
  }, [location.state]);

  return (
    <div
      className="Frame1000004012"
      style={{
        height: 'fit-content',
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        backgroundColor: 'transparent',
      }}
    >
      {/*
        Esqueleto COMPARTILHADO de página. `recepcao-conte-do` substitui o contêiner
        `.comiss-page-container`, que replicava à mão os mesmos valores (122px/82px, fundo
        cinza, 100vh). A classe local abaixo guarda só o respiro inferior, que o esqueleto
        compartilhado não tem.
      */}
      <div className="ConteDo recepcao-conte-do comiss-listas-conte-do">
        {error && <p className="comiss-error">{error}</p>}
        {/*
          O carregamento NÃO esconde mais a tela (era `FullScreenLoader` por cima de tudo,
          16/09/2026): cabeçalho, busca, abas e período continuam montados e utilizáveis, e
          só o corpo da tabela vira esqueleto. Mesma regra que a at-64/at-65 aplicou à faixa
          de erro — o estado transitório não pode apagar o que já está na tela.
        */}
        <div className="comiss-content">
            {/* Cabeçalho no padrão da Recepção: título + busca compartilhada, com o período
                à direita (o filtro da tela, como o Contac também faz). */}
            <div className="recepcao-header-row" style={{ justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
                <div className="recepcao-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  Comissionamentos
                  {/* O período fica VISÍVEL ao lado do título, como nos dashboards do
                      Contac; quem troca é o modal de Filtros (decisão do Victor, 16/09/2026). */}
                  <span className="PeriodoTitulo">{periodo.replace('-', '/')}</span>
                </div>

                {/* Busca: as classes são as da Recepção (`recepcao-search-*`) — os valores
                    estavam repetidos em estilo inline, divergindo da tela de referência. */}
                <div className="recepcao-search-bar">
                  <div className="ItemSearch" onClick={handleSearch}>
                    {/* O MESMO ícone da Recepção (`search-icon.svg`) — o SVG inline cinza
                        que estava aqui divergia da tela de referência. */}
                    <img className="imgIcon" src={SearchIcon} alt="Ícone de busca" width="20" height="20" />
                  </div>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    placeholder="Pesquise por código ou nome"
                    className="recepcao-search-input"
                  />
                </div>
              </div>

              {/* Carimbo encostado à direita do cabeçalho (decisão do Victor, 16/09/2026),
                  na mesma altura do título. O wrapper zera a margem inferior que o
                  componente carrega para as outras telas, onde ele fica em linha própria. */}
              <div className="comiss-carimbo-no-header">
                <DadosAtualizadosEm />
              </div>
            </div>

            {/* Abas no padrão da Recepção (`recepcao-tabs`), no lugar dos botões laranja
                com estilo inline. */}
            <div className="recepcao-tabs">
              {(['cooperativa', 'voluntario'] as TabType[]).map((tab) => (
                <button
                  key={tab}
                  type="button"
                  className={`recepcao-tab ${activeTab === tab ? 'active' : ''}`}
                  onClick={() => handleTabChange(tab)}
                >
                  {tab === 'voluntario' ? 'Voluntário' : 'Cooperativa'}
                </button>
              ))}
            </div>

            {/* Barra de controles da Recepção: itens por página + contador do recorte. */}
            <div className="VisualizarOrdenar">
              <div
                className="Frame1000004037"
                style={{ alignSelf: 'stretch', justifyContent: 'flex-start', alignItems: 'center', gap: 12, display: 'flex', flexWrap: 'wrap' }}
              >
                <div className="CtaBig">
                  <div className="Frame1000003987">
                    <div className="ComponentName">Itens por página:</div>
                    <select
                      value={limit}
                      onChange={(e) => {
                        setLimit(Number(e.target.value));
                        setPage(1);
                      }}
                      className="ComponentNameImgIcon"
                      style={{ cursor: 'pointer' }}
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </select>
                  </div>
                </div>
                <div className="CtaBig MouseHover" onClick={() => setFiltrosAbertos(true)}>
                  <div className="Frame1000003987">
                    <img src={Filter} alt="Filtro" width="19.80" height="19.80" />
                    <div className="ComponentName">Filtrar</div>
                  </div>
                </div>
                <div className="CtaQuantidadeDeCarros">
                  <div className="Frame1000003987">
                    {/* O contador é o único indício de que a lista está filtrada pela busca
                        — a faixa "Buscando por" saiu daqui, como a Recepção faz. */}
                    <div className="ComponentName">
                      {loading
                        ? 'Carregando...'
                        : `${filteredData.length === 0 ? 0 : (page - 1) * limit + 1}-${(page - 1) * limit + filteredData.length} de ${totalItens} ${activeTab === 'cooperativa' ? 'cooperativas' : 'voluntários'}`}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Tabela no casco compartilhado da Recepção (`TabelaScroll`/`TabelaContent`). */}
            <div
              style={{
                width: '100%',
                paddingTop: 30,
                background: 'var(--tema-superficie, white)',
                borderRadius: 16,
                border: '1px var(--tema-linha-2, #CACACA) solid',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                alignItems: 'center',
                display: 'flex',
                marginBottom: 20,
              }}
            >
              <div className="TabelaScroll" ref={tableScrollRef}>
                <div
                  className="TabelaContent"
                  style={{
                    ['--table-columns' as any]: tableColumns,
                    width: tableWidth,
                    minWidth: tableWidth,
                  }}
                  role="status"
                  aria-busy={loading}
                >
                  <div className="Tabela TabelaHeader">
                    <div className="Placa">
                      <div className="TTulo">
                        {headers.map((titulo, index) => (
                          <div
                            key={titulo}
                            className="Opcoes"
                            ref={(el) => {
                              headerRefs.current[index] = el;
                            }}
                          >
                            {titulo}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {loading ? (
                    <>
                      <span className="esqueleto-sr-only">Carregando a lista</span>
                      {/* Linhas na mesma grade da tabela real: o número de linhas
                          (8) é o que a lista costuma exibir, então a página não salta de
                          altura quando o dado chega. */}
                      {Array.from({ length: Math.min(limit, 8) }).map((_, i) => (
                        <div key={i} className="Tabela" aria-hidden="true">
                          <div className="Item"><div className="Data"><Esqueleto largura="48px" /></div></div>
                          <div className="Item"><div className="Data"><Esqueleto largura={i % 2 === 0 ? '70%' : '55%'} /></div></div>
                          <div className="Item"><div className="Data"><Esqueleto largura="36px" /></div></div>
                        </div>
                      ))}
                    </>
                  ) : filteredData.length === 0 ? (
                    <div className="Tabela">
                      <div className="Item" style={{ gridColumn: '1 / span 3' }}>
                        <div className="Data">Nenhum resultado encontrado.</div>
                      </div>
                    </div>
                  ) : (
                    filteredData.map((item) => (
                      <div key={item.id} className="Tabela">
                        <div className="Item">
                          <div className="Data">{item.codigo}</div>
                        </div>
                        <div className="Item">
                          {/* O nome é o caminho para o detalhe, como a placa é na Recepção —
                              a coluna "Ações" com 3 botões deixou de existir (16/09/2026). */}
                          <button
                            type="button"
                            className="Data comiss-link-nome"
                            title={`Abrir ${item.nome}`}
                            onClick={() => abrirDetalhe(item)}
                          >
                            {item.nome}
                          </button>
                        </div>
                        <div className="Item">
                          <div className="Data">
                            <span className={`contract-badge ${item.contratos === 0 ? 'zero' : ''}`}>
                              {item.contratos > 0 ? item.contratos : '—'}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
              {/* Paginação numerada compartilhada (components/paginacao), no lugar do par
                  Anterior/Próxima próprio do módulo. */}
              <Paginacao
                paginaAtual={page}
                totalPaginas={Math.max(1, totalPages)}
                onMudarPagina={setPage}
              />
            </div>
        </div>
      </div>
      {/* Montagem condicional: é o que faz o modal reabrir sempre com o filtro em vigor. */}
      {filtrosAbertos && (
        <FiltrosModal
          periodoAplicado={periodo}
          sidebarRef={sidebarRef}
          onAplicar={(novo) => {
            setPeriodo(novo);
            // Aplicar volta para a página 1: a página 3 do filtro anterior viraria uma
            // lista vazia sem explicação.
            setPage(1);
          }}
          onFechar={() => setFiltrosAbertos(false)}
        />
      )}
    </div>
  );
};

export default ComissionamentosPage;
